import { useState, useCallback, useEffect, useRef } from 'react'
import Box from '@mui/material/Box'
import ToggleButton from '@mui/material/ToggleButton'
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'

const MODELS = [
  { value: 'gemini-2.5-flash', label: 'gemini-2.5-flash' },
  { value: 'gemini-2.5-flash-lite', label: 'gemini-2.5-flash-lite' },
  { value: 'gemini-3.1-flash-lite', label: 'gemini-3.1-flash-lite' },
]

const PRICING_URL =
  'https://cloud.google.com/gemini-enterprise-agent-platform/generative-ai/pricing'

const DEFAULT_BUCKETS = {
  lowThreshold: 50,
  highThreshold: 150,
  lowBudget: 1024,
  midBudget: 2048,
  highBudget: 4096,
}

const inputSx = {
  backgroundColor: 'background.paper',
  border: 0,
  p: 1,
  borderWidth: 1,
  borderStyle: 'solid',
  borderColor: 'divider',
  borderRadius: '6px',
  fontSize: '0.85em',
  '&:focus': { borderColor: 'white', outline: 'none' },
}

const NumberInput = ({ value, onChange, placeholder, width = 70 }) => (
  <input
    type="number"
    min={0}
    value={value}
    onChange={(e) => onChange(e.target.value)}
    placeholder={placeholder}
    style={{ width, textAlign: 'center' }}
  />
)

const ModelAndThinkingConfig = ({
  model,
  onModelChange,
  onChange,
  compact,
  numCandidates,
  onNumCandidatesChange,
}) => {
  const [mode, setMode] = useState('flat')
  const [flatBudget, setFlatBudget] = useState('')
  const [buckets, setBuckets] = useState({ ...DEFAULT_BUCKETS })
  const prevRef = useRef(null)

  const getConfig = useCallback(() => {
    if (mode === 'flat') {
      const val = flatBudget.trim()
      return val === '' ? {} : { thinkingBudget: parseInt(val, 10) }
    }
    return {
      thinkingBuckets: {
        low_threshold: buckets.lowThreshold,
        high_threshold: buckets.highThreshold,
        low_budget: buckets.lowBudget,
        mid_budget: buckets.midBudget,
        high_budget: buckets.highBudget,
      },
    }
  }, [mode, flatBudget, buckets])

  useEffect(() => {
    const config = getConfig()
    const key = JSON.stringify(config)
    if (key !== prevRef.current) {
      prevRef.current = key
      onChange(config)
    }
  }, [getConfig, onChange])

  const updateBucket = useCallback((field, raw) => {
    const val = raw === '' ? 0 : parseInt(raw, 10) || 0
    setBuckets((prev) => ({ ...prev, [field]: val }))
  }, [])

  const labelSx = {
    fontSize: '0.78em',
    color: 'text.secondary',
    mb: 0.5,
    fontWeight: 600,
  }

  return (
    <Box sx={{ mt: compact ? 1 : 2 }}>
      <Box
        sx={{
          display: 'flex',
          gap: 2,
          alignItems: 'flex-end',
          flexWrap: 'wrap',
        }}
      >
        {/* Model selector */}
        <Box>
          <Box sx={labelSx}>Model</Box>
          <Box
            sx={{
              '& select': {
                ...inputSx,
                width: compact ? 180 : 220,
                color: 'text.primary',
                cursor: 'pointer',
              },
            }}
          >
            <select
              value={model}
              onChange={(e) => onModelChange(e.target.value)}
            >
              {MODELS.map((m) => (
                <option key={m.value} value={m.value}>
                  {m.label}
                </option>
              ))}
            </select>
          </Box>
        </Box>

        {/* Candidates toggle */}
        {onNumCandidatesChange && (
          <Box>
            <Box sx={labelSx}>Candidates</Box>
            <ToggleButtonGroup
              size="small"
              exclusive
              value={numCandidates || 3}
              onChange={(_, v) => v && onNumCandidatesChange(v)}
              sx={{ height: 30 }}
            >
              <ToggleButton
                value={1}
                sx={{
                  fontSize: '0.75em',
                  px: 1.2,
                  py: 0,
                  textTransform: 'none',
                }}
              >
                1
              </ToggleButton>
              <ToggleButton
                value={3}
                sx={{
                  fontSize: '0.75em',
                  px: 1.2,
                  py: 0,
                  textTransform: 'none',
                }}
              >
                3
              </ToggleButton>
            </ToggleButtonGroup>
            {numCandidates === 1 && (
              <Box
                sx={{
                  fontSize: '0.68em',
                  color: 'warning.main',
                  mt: 0.5,
                  maxWidth: 120,
                }}
              >
                Prompt auto-adjusted to single segment
              </Box>
            )}
          </Box>
        )}

        {/* Thinking mode toggle + input */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
            <Box sx={labelSx}>Thinking</Box>
            <ToggleButtonGroup
              size="small"
              exclusive
              value={mode}
              onChange={(_, v) => v && setMode(v)}
              sx={{ ml: 1, height: 22 }}
            >
              <ToggleButton
                value="flat"
                sx={{ fontSize: '0.7em', px: 1, py: 0, textTransform: 'none' }}
              >
                Flat
              </ToggleButton>
              <ToggleButton
                value="buckets"
                sx={{ fontSize: '0.7em', px: 1, py: 0, textTransform: 'none' }}
              >
                Buckets
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>

          <Box sx={{ '& input': inputSx }}>
            {mode === 'flat' ? (
              <NumberInput
                value={flatBudget}
                onChange={setFlatBudget}
                placeholder="auto"
                width={90}
              />
            ) : (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  flexWrap: 'wrap',
                  fontSize: '0.82em',
                }}
              >
                <Box sx={{ color: 'text.secondary' }}>&lt;</Box>
                <NumberInput
                  value={buckets.lowThreshold}
                  onChange={(v) => updateBucket('lowThreshold', v)}
                  width={50}
                />
                <Box sx={{ color: 'text.secondary' }}>words →</Box>
                <NumberInput
                  value={buckets.lowBudget}
                  onChange={(v) => updateBucket('lowBudget', v)}
                  width={60}
                />
                <Box sx={{ color: 'text.secondary', ml: 1 }}>&lt;</Box>
                <NumberInput
                  value={buckets.highThreshold}
                  onChange={(v) => updateBucket('highThreshold', v)}
                  width={50}
                />
                <Box sx={{ color: 'text.secondary' }}>words →</Box>
                <NumberInput
                  value={buckets.midBudget}
                  onChange={(v) => updateBucket('midBudget', v)}
                  width={60}
                />
                <Box sx={{ color: 'text.secondary', ml: 1 }}>≥</Box>
                <NumberInput
                  value={buckets.highThreshold}
                  onChange={(v) => updateBucket('highThreshold', v)}
                  width={50}
                />
                <Box sx={{ color: 'text.secondary' }}>→</Box>
                <NumberInput
                  value={buckets.highBudget}
                  onChange={(v) => updateBucket('highBudget', v)}
                  width={60}
                />
              </Box>
            )}
          </Box>
        </Box>
      </Box>

      <Box
        component="a"
        href={PRICING_URL}
        target="_blank"
        rel="noopener noreferrer"
        sx={{
          fontSize: '0.72em',
          color: 'text.secondary',
          mt: 0.75,
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.4,
          textDecoration: 'none',
          '&:hover': { textDecoration: 'underline' },
        }}
      >
        Pricing &amp; models
        <OpenInNewIcon sx={{ fontSize: '1.1em' }} />
      </Box>
    </Box>
  )
}

export default ModelAndThinkingConfig
